import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { listPersonas, impersonate, type Persona } from '../../lib/personas';
import { ApiError } from '../../lib/apiClient';
import { useAuth } from '../../auth/useAuth';
import { HOME_ROUTE_FOR_ROLE } from '../../routes/roleHome';
import type { Role } from '../../auth/types';
import { formatDateShort } from '../../lib/format';
import './AdminPages.css';

/**
 * The test-persona roster, and the "Login as" action.
 *
 * ONLY personas appear here. The engine will not impersonate anything without
 * is_test_persona, which migration 045 welds to the reserved @voleto.internal domain —
 * so there is no real account this screen could offer, and no input it could be given
 * that would reach one. The list is separate from Users for exactly that reason: putting
 * a "Login as" button in a list of real customers, greyed out for most rows, is one
 * conditional away from being a customer-impersonation feature.
 *
 * The badges spell out each persona's GATE STATE rather than just its role, because that
 * is what decides what the persona will actually see — an approved investor with no network
 * relationship and one with a backdated relationship look identical until you say so.
 */

function GateBadges({ persona }: { persona: Persona }) {
  if (persona.role !== 'investor') return null;
  return (
    <div className="persona__badges">
      <span className={persona.investorApproved ? 'badge badge--ok' : 'badge badge--pending'}>
        {persona.investorApproved ? 'Approved' : 'Not approved'}
      </span>
      <span className={persona.accredited ? 'badge badge--ok' : 'badge badge--muted'}>
        {persona.accredited ? 'Accredited' : 'Not accredited'}
      </span>
      <span className={persona.networkTagged ? 'badge badge--network' : 'badge badge--muted'}>
        {persona.networkTagged
          ? `506(b) network since ${formatDateShort(persona.networkTaggedAt)}`
          : '506(c) only'}
      </span>
    </div>
  );
}

export function AdminPersonas() {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    listPersonas()
      .then((rows) => {
        setPersonas(rows);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not load personas.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  async function handleLoginAs(persona: Persona) {
    setBusy(persona.userId);
    setError(null);
    try {
      await impersonate(persona.userId);
      // Re-ask the engine who we are, so the banner renders from the server's answer.
      await refresh();
      // Drop the admin straight into the portal that persona belongs to, which is where
      // their experience of the platform actually starts.
      navigate(HOME_ROUTE_FOR_ROLE[persona.role as Role] ?? '/');
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not start the session.');
      setBusy(null);
    }
  }

  return (
    <div className="admin-page">
      <header className="admin-page__header">
        <h1>Test personas</h1>
        <p className="admin-page__lede">
          Permanent fixture accounts, one per role and gate state. Signing in as one shows
          you exactly what a real user in that state sees, every gate runs normally, and
          nothing here grants access.
        </p>
      </header>

      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}

      {loading && <p className="admin-page__state">Loading…</p>}

      {!loading && personas.length === 0 && (
        <div className="admin-page__state">
          <p>
            No personas found. Run <code>node scripts/seed-test-personas.mjs</code> against
            this environment.
          </p>
        </div>
      )}

      <div className="persona-list">
        {personas.map((persona) => (
          <article key={persona.userId} className="persona">
            <div className="persona__main">
              <h2 className="persona__label">{persona.label}</h2>
              <p className="persona__email mono">{persona.email}</p>
              <GateBadges persona={persona} />
            </div>

            <div className="persona__action">
              <span className="persona__role mono">{persona.role}</span>
              <button
                type="button"
                className="button button--primary"
                onClick={() => void handleLoginAs(persona)}
                disabled={busy != null || persona.suspended}
              >
                {busy === persona.userId ? 'Starting…' : 'Login as'}
              </button>
            </div>
          </article>
        ))}
      </div>

      <p className="admin-page__note">
        Sessions last 60 minutes and cannot be renewed. Your own admin session is untouched
, returning is a single click on the banner. Every start and stop is written to the
        audit log.
      </p>
    </div>
  );
}
